import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	ViewChild,
	effect,
	inject,
	input,
	signal,
	computed,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { RaceSessionService, ThemeService, TimingTowerService } from '@vbrick/vb-lxp-lib';

import { CarManagerService, CarState } from './car-manager.service';
import { GpsService } from './gps.service';
import { TrackRendererService } from './track-renderer.service';

@Component({
	selector: 'mini-map',
	templateUrl: './mini-map.component.html',
	providers: [GpsService],
})
export class MiniMapComponent implements AfterViewInit, OnDestroy {
	@ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

	private gpsService = inject(GpsService);
	private carManager = inject(CarManagerService);
	private trackService = inject(TrackRendererService);
	private themeService = inject(ThemeService);
	private raceSessionService = inject(RaceSessionService);

	track = input('');
	sampleGps = input('');
	entries = computed(() => this.raceSessionService.raceTeamsPosition());

	private canvasReady = signal(false);
	private colors = ['red', 'orange', 'yellow', 'green', 'blue', 'indigo', 'violet'];
	private ctx!: CanvasRenderingContext2D;
	private gpsSub: Subscription | null = null;
	private lastTimestamp: number | null = null;
	private rafId: number | null = null;

	simulationEffect = effect(async () => {
		if (!this.canvasReady() || !this.track() || !this.sampleGps()) return;
		await this.restartSimulation(this.track(), this.sampleGps());
	});

	async ngAfterViewInit() {
		this.ctx = this.canvas.nativeElement.getContext('2d')!;
		await this.trackService.loadWasm();

		this.startRenderLoop();
		this.canvasReady.set(true);
	}

	ngOnDestroy(): void {
		this.carManager.reset();
		this.gpsSub?.unsubscribe();

		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}

		this.lastTimestamp = null;
	}

	private async restartSimulation(trackPath: string, gpsPath: string) {
		this.gpsSub?.unsubscribe();
		this.gpsSub = null;

		this.lastTimestamp = null;
		this.carManager.reset();

		await this.trackService.loadTrack(trackPath);
		this.gpsSub = this.gpsService.getSimulatedGpsUpdates(gpsPath, 400, 10).subscribe((update) => {
			const carNumbers = this.entries().map((e) => e.number);
			let i = 0;
			for (const [lon, lat] of update) {
				const [x, y] = this.trackService.latLonToXY(lat, lon);
				const label = carNumbers[i];
				this.carManager.updateCarGps(`${label}`, x, y, this.colors[i % this.colors.length]);
				i++;
			}
		});
	}

	private startRenderLoop() {
		if (this.rafId !== null) return;

		const loop = (ts: number) => {
			this.renderFrame(ts);
			this.rafId = requestAnimationFrame(loop);
		};

		this.rafId = requestAnimationFrame(loop);
	}

	private renderFrame(timestamp: number) {
		if (!this.lastTimestamp) this.lastTimestamp = timestamp;
		const deltaTime = Math.min(timestamp - this.lastTimestamp, 50);
		this.lastTimestamp = timestamp;

		const ctx = this.ctx;
		const fillColor = this.themeService.isDarkMode() ? 'black' : 'white';
		ctx.fillStyle = fillColor;
		ctx.fillRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);

		this.drawTrack();
		this.carManager.updatePositions(deltaTime);
		for (const car of this.carManager.getCars()) {
			this.drawCar(car);
		}
	}

	private drawTrack() {
		const points = this.trackService.getPoints();
		if (points.length < 2) return;

		const { scale, offsetX, offsetY, minX, minY } = this.getTrackTransform();
		const ctx = this.ctx;

		ctx.save();
		ctx.translate(offsetX, offsetY);
		ctx.scale(scale, scale);
		ctx.translate(-minX, -minY);

		const trackStrokeColor = this.themeService.isDarkMode() ? 'white' : 'black';
		ctx.strokeStyle = trackStrokeColor;
		ctx.lineWidth = 4 / scale;
		ctx.beginPath();
		ctx.moveTo(points[0], points[1]);
		for (let i = 2; i < points.length; i += 2) {
			ctx.lineTo(points[i], points[i + 1]);
		}
		ctx.stroke();
		ctx.restore();
	}

	private getTrackTransform() {
		const points = this.trackService.getPoints();
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity;
		for (let i = 0; i < points.length; i += 2) {
			const x = points[i],
				y = points[i + 1];
			if (x < minX) minX = x;
			if (y < minY) minY = y;
			if (x > maxX) maxX = x;
			if (y > maxY) maxY = y;
		}
		const width = maxX - minX;
		const height = maxY - minY;
		const padding = 50;
		const scale = Math.min(
			(this.canvas.nativeElement.width - 2 * padding) / width,
			(this.canvas.nativeElement.height - 2 * padding) / height,
		);
		const offsetX = (this.canvas.nativeElement.width - width * scale) / 2;
		const offsetY = (this.canvas.nativeElement.height - height * scale) / 2;
		return { scale, offsetX, offsetY, minX, minY, maxX, maxY };
	}

	private drawCar(car: CarState) {
		const ctx = this.ctx;
		const { scale, offsetX, offsetY, minX, minY } = this.getTrackTransform();

		ctx.save();
		ctx.translate(offsetX + 0.5, offsetY + 0.5);
		ctx.scale(scale, scale);
		ctx.translate(-minX, -minY);

		// Draw car dot
		if (car.selected) {
			ctx.fillStyle = car.color;
			ctx.beginPath();
			ctx.arc(car.x, car.y, 6 / scale, 0, Math.PI * 2);
			ctx.fill();
		} else {
			ctx.beginPath();
			ctx.arc(car.x, car.y, 5 / scale, 0, Math.PI * 2);

			// optional light fill
			ctx.fillStyle = car.color;
			ctx.globalAlpha = 0.4;
			ctx.fill();
			ctx.globalAlpha = 1;

			// border
			ctx.strokeStyle = car.color;
			ctx.lineWidth = 1 / scale;
			ctx.stroke();
		}

		if (car.selected) {
			// Draw car label
			const labelOffsetY = -10 / scale;
			ctx.font = `${16 / scale}px sans-serif`;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'bottom';
			// Label background
			const text = car.id;
			const metrics = ctx.measureText(text);
			const padding = 4 / scale;

			ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
			ctx.fillRect(
				car.x - metrics.width / 2 - padding,
				car.y + labelOffsetY - 12 / scale - padding,
				metrics.width + padding * 2,
				12 / scale + padding * 2,
			);

			ctx.fillStyle = 'white';
			ctx.fillText(car.id, car.x, car.y + labelOffsetY);
		}
		ctx.restore();
	}
}
